import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { Page_BaseManagerUI_SingleType_Proto, Renderer_SearchResult, SearchItemV3_With_ID, SearchSelector_BaseManagerUI } from '@app/styles';
import { Component_GenericRenderer, EditableDBItemV3, ModuleFE_Toaster } from '@nu-art/thunderstorm-frontend';
import { removeDBObjectKeys } from '@nu-art/ts-common';
import { TS_HorizontalWorkspace } from '@nu-art/ts-workspace-frontend';
import { AccessLevel, ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { AllExpressionTypes, BuilderTypesDbKeys, DBDef_Expression } from '@app/hcs-shared';
import { RoutePermissions } from '@nu-art/permissions-frontend';
import { ModuleFE_Expression, ModulePackFE_Expression } from '../../../../_entity/expression/index.js';
import { ModuleFE_Variable } from '../../../../_entity/variable/index.js';
import { ModuleFE_Value } from '../../../../_entity/value/index.js';
import { ModuleFE_Reference } from '../../../../_entity/reference/index.js';
import { ModuleFE_Tag } from '../../../../_entity/tag/index.js';
import { workspaceKey_ExpressionSearch } from '../../workspace-config.js';
import { Component_ExpressionBuilder } from '../expression-builder/Component_ExpressionBuilder.js';
import { Panel_ExpressionSidePanel } from '../expression-builder/components/index.js';
import { hasDuplicateNameExpression } from '../../../../utils.js';
import { QPageMessage } from '../../../q-components/QPageMessage/QPageMessage.js';
const ManagerKey_Expression = 'expression-manager';
export class Page_ExpressionManager extends Page_BaseManagerUI_SingleType_Proto {
    // ######################## Static ########################
    static Route = {
        key: ManagerKey_Expression,
        path: 'expressions',
        Component: this,
        enabled: RoutePermissions(ModuleFE_Expression.permissions.ui.Release),
        modulesToAwait: [...ModulePackFE_Expression, ModuleFE_Variable, ModuleFE_Value, ModuleFE_Reference],
    };
    static searchItemKey_Expression = ModuleFE_Expression.dbDef.dbKey;
    static SearchableItem_Expression = SearchItemV3_With_ID({
        module: ModuleFE_Expression,
        type: Page_ExpressionManager.searchItemKey_Expression,
        route: Page_ExpressionManager.Route,
        entityName: ModuleFE_Expression.dbDef.entityName,
        entityLabel: 'Expression',
        groupId: ['Advisor'],
        mapper: (item) => [(item.label && item.label.length) ? item.label : 'Missing Label'],
        tags: [],
        filter: (expression) => BuilderTypesDbKeys.includes(expression.expressionType),
        renderer: (item, selected, onClickCallback, getRightClickActions) => {
            const tags = ModuleFE_Tag.getTagsLabelByTagGroups(item.tagGroups);
            const editableExpression = new EditableDBItemV3(item, ModuleFE_Expression);
            try {
                editableExpression.validate();
            }
            catch (err) {
                console.error(err);
            }
            return _jsx(Renderer_SearchResult, { itemId: item._id, tags: tags, error: editableExpression.hasErrors() || hasDuplicateNameExpression(item), itemKey: Page_ExpressionManager.searchItemKey_Expression, itemLabel: item.label ?? item.description ?? 'Missing Label', entityLabel: 'Expression', hierarchy: AllExpressionTypes.find(type => type.dbKey === item.expressionType)?.label ?? 'Missing Type', onClickCallback: onClickCallback, getRightClickActions: getRightClickActions, orgRenderer: _jsx(Component_GenericRenderer, { rendererKey: 'organization-permission-editor', props: {
                        item: item,
                        indicatorMode: true,
                        orgNameClassName: 'search-result__hierarchy'
                    } }), selected: selected }, item._id);
        },
        resultRenderer: (item) => {
            return _jsx(_Fragment, {});
        }
    });
    static defaultProps = {
        searchItems: [Page_ExpressionManager.SearchableItem_Expression],
        autoSave: false
    };
    // ######################## Abstract Implementations ########################
    getInitialObject() {
        return Object.freeze({
            terms: [],
            expression: 'T1',
            isEvidenceBased: false
        });
    }
    workspace = () => ({
        id: ManagerKey_Expression,
        key: workspaceKey_ExpressionSearch,
        renderers: {
            horizontalSpace: TS_HorizontalWorkspace,
        },
        instances: {
            // search instances
            searchPanel: this.renderLeftPanel(),
            dataViewer: this.renderRightPanel(),
        }
    });
    renderLeftPanel = () => {
        if (this.state.selectedItem)
            return _jsx(Component_ExpressionBuilder, { deselectExpression: this.deselectExpression, editable: this.state.selectedItem, copyExpression: this.onCopyExpression });
        const accessLevel = this.getAccessLevel();
        const buttons = AccessLevel.HasAccess === accessLevel ? [{
                label: 'Add Expression',
                action: this.onCreateNewExpression
            }] : [];
        return _jsx(SearchSelector_BaseManagerUI, { selectedItem: this.state.selectedResult, onSearchItemSelected: (item) => this.onSearchResultSelected(item.id, item.type), searchItems: [Page_ExpressionManager.SearchableItem_Expression], getRightClickActions: (dbKey, itemId) => {
                if (dbKey !== DBDef_Expression.dbKey)
                    return [];
                return [{
                        actionLabel: 'Copy Expression',
                        actionCallback: () => this.onCopyExpression(itemId)
                    }];
            }, buttons: buttons });
    };
    renderRightPanel = () => {
        if (this.state.selectedItem)
            return _jsx(Panel_ExpressionSidePanel, { editable: this.state.selectedItem });
        return _jsx(QPageMessage, { message: 'Expression Manager' });
    };
    // ######################## Logic ########################
    onCreateNewExpression = () => {
        this.clearSelectedResult();
        const newExpression = this.getEditableItem();
        newExpression.item.tagGroups ??= [];
        //disable auto save
        newExpression.setAutoSave(false);
        this.setState({
            selectedItem: newExpression,
            selectedResult: undefined
        });
    };
    onCopyExpression = async (expressionId) => {
        const expressionToCopy = ModuleFE_Expression.cache.unique(expressionId);
        if (!expressionToCopy) {
            ModuleFE_Toaster.toastError('Could not find expression to copy');
            return;
        }
        const data = { ...removeDBObjectKeys(expressionToCopy), label: expressionToCopy.label + ' - Copy' };
        try {
            const newExpression = await ModuleFE_Expression.v1.upsert(data).executeSync();
            this.onSearchResultSelected(newExpression._id, DBDef_Expression.dbKey);
        }
        catch (err) {
            this.logError(err);
        }
    };
    getAccessLevel() {
        return ModuleFE_PermissionsAssert.getAccessLevel(ModuleFE_Expression.permissions.collection.edit);
    }
    deselectExpression = () => {
        this.setState({
            selectedItem: undefined,
            selectedResult: undefined
        }, () => {
            this.clearSelectedResult();
        });
    };
}
//# sourceMappingURL=Page_ExpressionManager.js.map